/**
 * JWT Utilities
 *
 * Centralized JWT decoding utilities using jwt-decode library.
 * Provides type-safe token decoding with proper error handling.
 */

import { jwtDecode } from "jwt-decode";
import type { ProjectTokenClaims } from "@/api/auth/auth.types";

/**
 * Standard JWT payload fields
 */
export interface JwtStandardPayload {
  /** Subject (usually user ID or email) */
  sub?: string;
  /** Issued at timestamp */
  iat?: number;
  /** Expiration timestamp */
  exp?: number;
  /** Not before timestamp */
  nbf?: number;
  /** Issuer */
  iss?: string;
  /** Audience */
  aud?: string | string[];
  /** JWT ID */
  jti?: string;
}

/**
 * Notifyy-specific JWT token payload
 * Contains user information extracted from auth tokens
 */
export interface NotifyyTokenPayload extends JwtStandardPayload {
  // User identification
  role?: string;
  isAgent?: boolean;
  emailId?: string;
  email?: string;
  hash?: string;
  status?: string;

  // User name fields (directly in token)
  firstName?: string;
  lastName?: string;

  // Google OAuth fields (when using Google login)
  picture?: string;
  name?: string;
  given_name?: string;
  family_name?: string;

  // Microsoft OAuth fields
  preferred_username?: string;

  // Organization fields
  ciNo?: string;
  cin?: string;
}

/**
 * Extracted user data from JWT token
 */
export interface JwtUserData {
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
  isAgent?: boolean;
  status?: string;
  ciNo?: string;
  profilePicUrl?: string;
}

/**
 * Result of JWT decode operation
 */
export interface JwtDecodeResult<T = NotifyyTokenPayload> {
  success: boolean;
  payload: T | null;
  error?: string;
  isExpired?: boolean;
}

/**
 * Decode a JWT token and return the payload
 *
 * @param token - The JWT token string to decode
 * @returns The decoded result with payload or error
 *
 * @example
 * const result = decodeToken(refreshToken);
 * if (result.success && result.payload) {
 *   console.log(result.payload.firstName);
 * }
 */
export function decodeToken<T = NotifyyTokenPayload>(
  token: string | null | undefined
): JwtDecodeResult<T> {
  // Validate input
  if (!token || typeof token !== "string") {
    return {
      success: false,
      payload: null,
      error: "Invalid token: Token is empty or not a string",
    };
  }

  // Basic JWT format validation (should have 3 parts separated by dots)
  const parts = token.split(".");
  if (parts.length !== 3) {
    return {
      success: false,
      payload: null,
      error: "Invalid token format: JWT should have 3 parts",
    };
  }

  try {
    const payload = jwtDecode<T>(token);

    // Check if token is expired
    const jwtPayload = payload as JwtStandardPayload;
    const isExpired = jwtPayload.exp ? jwtPayload.exp * 1000 < Date.now() : false;

    return {
      success: true,
      payload,
      isExpired,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown decode error";
    return {
      success: false,
      payload: null,
      error: `Failed to decode token: ${errorMessage}`,
    };
  }
}

/**
 * Check if a JWT token is expired
 *
 * @param token - The JWT token to check
 * @returns true if expired, false if valid, null if unable to determine
 */
export function isTokenExpired(token: string | null | undefined): boolean | null {
  const result = decodeToken(token);
  if (!result.success || !result.payload) {
    return null;
  }
  return result.isExpired ?? null;
}

/**
 * Get the expiration time of a JWT token
 *
 * @param token - The JWT token
 * @returns Date object of expiration, or null if invalid/no expiration
 */
export function getTokenExpiration(token: string | null | undefined): Date | null {
  const result = decodeToken(token);
  if (!result.success || !result.payload) {
    return null;
  }

  const payload = result.payload as JwtStandardPayload;
  if (payload.exp) {
    return new Date(payload.exp * 1000);
  }
  return null;
}

/**
 * Extract user data from a Notifyy JWT token
 *
 * Handles multiple token formats:
 * - Direct firstName/lastName fields
 * - Google OAuth fields (given_name, family_name, name)
 * - Microsoft OAuth fields (preferred_username)
 *
 * @param token - The JWT token (access or refresh)
 * @returns Extracted user data or null if extraction failed
 */
export function extractUserDataFromToken(
  token: string | null | undefined
): JwtUserData | null {
  const result = decodeToken<NotifyyTokenPayload>(token);

  if (!result.success || !result.payload) {
    return null;
  }

  const payload = result.payload;

  // Extract firstName with fallbacks
  const firstName =
    payload.firstName ||
    payload.given_name ||
    (payload.name ? payload.name.split(" ")[0] : "") ||
    "";

  // Extract lastName with fallbacks
  const lastName =
    payload.lastName ||
    payload.family_name ||
    (payload.name && payload.name.includes(" ")
      ? payload.name.split(" ").slice(1).join(" ")
      : "") ||
    "";

  // Extract email with fallbacks
  const email =
    payload.emailId ||
    payload.email ||
    payload.sub ||
    payload.preferred_username ||
    "";

  // Extract profile picture (Google OAuth)
  const profilePicUrl = payload.picture || undefined;

  // Extract CIN
  const ciNo = payload.ciNo || payload.cin || undefined;

  return {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    email: email.trim(),
    role: payload.role,
    isAgent: payload.isAgent,
    status: payload.status,
    ciNo,
    profilePicUrl,
  };
}

/**
 * Safely decode a token and extract user data without throwing
 *
 * @param token - The JWT token
 * @returns Object with user data and any error message
 */
export function safeExtractUserData(token: string | null | undefined): {
  data: JwtUserData | null;
  error: string | null;
} {
  try {
    const data = extractUserDataFromToken(token);
    return { data, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error extracting user data";
    return { data: null, error: errorMessage };
  }
}

/**
 * Get remaining time until token expiration in milliseconds
 *
 * @param token - The JWT token
 * @returns Milliseconds until expiration, 0 if expired, null if invalid
 */
export function getTokenRemainingTime(token: string | null | undefined): number | null {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return null;
  }

  const remaining = expiration.getTime() - Date.now();
  return remaining > 0 ? remaining : 0;
}

/**
 * Check if token will expire within a given threshold
 *
 * @param token - The JWT token
 * @param thresholdMs - Threshold in milliseconds (default: 5 minutes)
 * @returns true if token will expire within threshold
 */
export function isTokenExpiringSoon(
  token: string | null | undefined,
  thresholdMs: number = 5 * 60 * 1000
): boolean {
  const remaining = getTokenRemainingTime(token);
  if (remaining === null) {
    return true; // Treat invalid tokens as expiring
  }
  return remaining < thresholdMs;
}

// ============================================
// PROJECT TOKEN CLAIMS
// ============================================

/**
 * Raw JWT payload shape for project-scoped access tokens.
 * Maps directly to the backend JWT structure.
 */
interface ProjectTokenJwtPayload extends JwtStandardPayload {
  agentId?: number;
  user_id?: number;
  tv?: number;
  role?: string;
  roles?: string[];
  cin?: string;
  type?: string;
  typ?: string;
  projectId?: number;
  project_id?: number;
  superAdmin?: boolean;
  super_admin?: boolean;
  hash?: string;
}

/**
 * Decode a project-scoped access token and extract structured claims.
 *
 * Returns `null` for any invalid, missing, or malformed token —
 * callers never need to handle exceptions.
 *
 * @param token - The project access token JWT string
 * @returns Structured ProjectTokenClaims or null
 */
export function decodeProjectTokenClaims(
  token: string | null | undefined,
): ProjectTokenClaims | null {
  const result = decodeToken<ProjectTokenJwtPayload>(token);

  if (!result.success || !result.payload) {
    return null;
  }

  const p = result.payload;
  const agentId = p.agentId ?? p.user_id;
  const projectId = p.projectId ?? p.project_id;
  const role = p.role ?? p.roles?.[0];
  const type = p.type ?? p.typ ?? "PROJECT";
  const superAdmin = p.superAdmin === true || p.super_admin === true;

  // Validate required fields — reject if critical fields are missing
  if (
    typeof agentId !== "number" ||
    typeof projectId !== "number" ||
    !p.cin ||
    !role
  ) {
    return null;
  }

  return {
    agentId,
    role,
    cin: p.cin,
    type,
    projectId,
    superAdmin,
    email: p.sub ?? "",
    issuedAt: p.iat ?? 0,
    expiresAt: p.exp ?? 0,
  };
}
